/**
 * useMapLinks — mention lines + Use echoes for the map
 *
 * Place at: src/hooks/useMapLinks.js
 * Used by:  src/pages/Dashboard.jsx -> passed to CosmicMap
 *
 * Fetches two independent things and FAILS QUIET on each:
 *   mentionEdges  GET /api/mentions/map   [{id, sourceId, targetId}]
 *                 404 when ENABLE_PROJECT_MENTIONS is off -> []
 *   useEchoes     GET /api/uses/map       [{id, projectId, zoneId, note, cohortId}]
 *                 404 when ENABLE_PROJECT_USES is off -> []
 *
 * showLinks  — the bottom-left "Show links" pill. MENTION LINES ONLY.
 *              On: every mention line is faintly visible. Off: mention lines
 *              appear only for the project you click.
 * showEchoes — the "Show Use echoes" switch in the filter menu. Echoes are
 *              opt-in: with it off they are never drawn, not even for a
 *              selected project.
 * Both remembered per browser; storage failures are ignored (private windows).
 *
 * refreshKey: pass something that changes when map data reloads (Dashboard
 * passes the projects array) so a newly saved [[p:...]] link shows up after
 * create/edit without a page refresh.
 */

import { useCallback, useEffect, useState } from 'react';
import { mentionsAPI, usesAPI } from '../api';

const LINKS_KEY = 'enume_map_show_links';
const ECHOES_KEY = 'enume_map_show_echoes';

const readFlag = (key) => {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
};
const writeFlag = (key, value) => {
  try { localStorage.setItem(key, value ? '1' : '0'); } catch { /* ignore */ }
};

export default function useMapLinks({ enabled = true, refreshKey } = {}) {
  const [mentionEdges, setMentionEdges] = useState([]);
  const [useEchoes, setUseEchoes] = useState([]);
  const [showLinks, setShowLinksState] = useState(() => readFlag(LINKS_KEY));
  const [showEchoes, setShowEchoesState] = useState(() => readFlag(ECHOES_KEY));

  useEffect(() => {
    if (!enabled) { setMentionEdges([]); setUseEchoes([]); return undefined; }
    let cancelled = false;
    mentionsAPI.mapEdges()
      .then((res) => { if (!cancelled) setMentionEdges(res.data?.edges || []); })
      .catch(() => { if (!cancelled) setMentionEdges([]); });
    usesAPI.mapEchoes()
      .then((res) => { if (!cancelled) setUseEchoes(res.data?.echoes || []); })
      .catch(() => { if (!cancelled) setUseEchoes([]); });
    return () => { cancelled = true; };
  }, [enabled, refreshKey]);

  const setShowLinks = useCallback((next) => {
    setShowLinksState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writeFlag(LINKS_KEY, value);
      return value;
    });
  }, []);

  const setShowEchoes = useCallback((next) => {
    setShowEchoesState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      writeFlag(ECHOES_KEY, value);
      return value;
    });
  }, []);

  return {
    mentionEdges,
    useEchoes,
    showLinks,
    setShowLinks,
    showEchoes,
    setShowEchoes,
    hasMentions: mentionEdges.length > 0,
    echoCount: useEchoes.length,
  };
}